/**
 * Readiness Definition Types
 *
 * Defines what "ready for ranking" means for items in a given context.
 * A ReadinessDefinition is configured at the workspace level and inherited
 * down through cycle → track → session (later scopes override earlier).
 *
 * Inheritance resolver: session ?? track ?? cycle ?? workspace
 *
 * Criterion types:
 *   field_populated  — structural field on the item must be non-null
 *   attribute_values — scoring attribute must be set (optionally to specific values)
 *   agent_signoff    — a named agent must have signed off on the item
 */

// ============================================================================
// Criterion Types
// ============================================================================

/**
 * A structural field on the item must be non-null / non-empty.
 * fieldKey matches the STRUCTURAL_FIELD_METAS keys (e.g. 'ownerId', 'altitudeLevel').
 */
export interface FieldPopulatedCriterion {
  type: 'field_populated';
  /** Stable field key — matches STRUCTURAL_FIELD_METAS.fieldKey */
  fieldKey: string;
  /** Optional display label override */
  label?: string;
}

/**
 * A scoring attribute must have an accepted value.
 * When requiredValues is set, the accepted value must be one of those values.
 */
export interface AttributeValuesCriterion {
  type: 'attribute_values';
  /** Attribute key (from the attributes table) */
  attributeKey: string;
  /** Optional display label override */
  label?: string;
  /**
   * Optional whitelist of accepted value strings.
   * If empty or undefined, any non-null value satisfies the criterion.
   */
  requiredValues?: string[];
}

/**
 * A named agent must have signed off on the item.
 * Agent sign-offs are tracked as agent_run records with matching agentKey.
 */
export interface AgentSignoffCriterion {
  type: 'agent_signoff';
  /** Agent key identifier (e.g. 'scribe', 'twins', 'atc') */
  agentKey: string;
  /** Display label for this criterion */
  label?: string;
}

export type ReadinessCriterion =
  | FieldPopulatedCriterion
  | AttributeValuesCriterion
  | AgentSignoffCriterion;

// ============================================================================
// Definition
// ============================================================================

/** Readiness definition — a named set of criteria stored at workspace level. */
export interface ReadinessDefinition {
  id: string;
  workspaceId: string;
  name: string;
  description?: string | null;
  criteria: ReadinessCriterion[];
  createdBy?: string | null;
  createdAt: string;
  updatedAt: string;
}

/** DB row shape from the readiness_definitions table */
export interface ReadinessDefinitionRow {
  id: string;
  workspace_id: string;
  name: string;
  description: string | null;
  criteria: ReadinessCriterion[];
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export function rowToReadinessDefinition(row: ReadinessDefinitionRow): ReadinessDefinition {
  return {
    id: row.id,
    workspaceId: row.workspace_id,
    name: row.name,
    description: row.description,
    criteria: Array.isArray(row.criteria) ? row.criteria : [],
    createdBy: row.created_by,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

// ============================================================================
// Evaluation Result
// ============================================================================

/**
 * Who is responsible for resolving this criterion when it fails.
 * Used by ReadinessPage to surface actionable "who can fix this" guidance.
 */
export type RemediationRole =
  | 'Item owner'
  | 'Cycle owner'
  | 'Track owner'
  | 'Any participant'
  | 'Workspace admin';

/**
 * What kind of work is required to resolve this criterion.
 * Distinguishes planning prerequisites from governance/configuration ones.
 */
export type RemediationType =
  | 'directional_planning'  // Needs Directional Planning signals before this can pass
  | 'detailed_planning'     // Needs Detailed Planning (post-commitment only)
  | 'configuration'         // Cycle/Track/session configuration action
  | 'governance';           // Requires Catchball or governance action

/** Per-criterion result */
export interface CriterionResult {
  criterion: ReadinessCriterion;
  /** Whether this criterion is satisfied */
  pass: boolean;
  /** Human-readable explanation when not passing */
  reason?: string;
  /**
   * Who is responsible for resolving this criterion.
   * Populated by the evaluator based on the criterion definition.
   */
  remediationRole?: RemediationRole;
  /**
   * What type of action resolves this criterion.
   * Helps the user understand whether they need to do planning work,
   * configuration work, or governance work.
   */
  remediationType?: RemediationType;
  /**
   * True when the criterion comes from the workspace's every-item rules (a Hard
   * Stop in Settings → Readiness → Every item) rather than from the readiness
   * definition itself. Every readiness definition includes those rules.
   */
  everyItem?: boolean;
}

/** Full readiness evaluation result for a single item against a definition */
export interface ReadinessEvalResult {
  /** Item ID that was evaluated */
  itemId: string;
  /** Definition ID that was used; null when only the every-item rules applied */
  definitionId: string | null;
  /** Whether all criteria passed */
  pass: boolean;
  /** Per-criterion breakdown */
  criteria: CriterionResult[];
  /** Number of passing criteria */
  passCount: number;
  /** Total number of criteria */
  totalCount: number;
}

// ============================================================================
// Inheritance
// ============================================================================

/** Which layer provided the effective readiness definition */
export type ReadinessDefinitionScope = 'workspace' | 'cycle' | 'track' | 'session';

export interface EffectiveReadinessDefinition {
  definition: ReadinessDefinition | null;
  source: ReadinessDefinitionScope | 'none';
}
